/**
 * Drop legacy / shell fields from node objects before they reach @xyflow/svelte.
 * `dragHandle` + falsey `draggable` break whole-card dragging; XYFlow may also
 * leave `dragging` on the object after a drag cycle.
 */
export function omitLegacyDragHandle(node: FlowNode): FlowNode {
	const {
		dragHandle: _dh,
		draggable: _dr,
		dragging: _dg,
		...rest
	} = node as FlowNode & { dragHandle?: string; draggable?: boolean; dragging?: boolean };
	return rest as FlowNode;
}

// Flow node types and interfaces
export type NodeType =
	// Core types
	| 'trigger'
	| 'condition'
	| 'action'
	// Condition subtypes
	| 'timeCondition'
	| 'manualCondition'
	| 'weatherCondition'
	| 'sunriseSunsetCondition'
	| 'energyCondition'
	| 'mathCondition'
	| 'sceneCondition'
	| 'alarmCondition'
	| 'scheduleCondition'
	// Action subtypes
	| 'notificationAction'
	| 'groupAction'
	| 'sceneAction'
	| 'alarmAction'
	| 'scriptAction'
	// Consolidated nodes
	| 'logicGate'
	| 'timer'
	| 'hysteresisCondition'
	| 'actionSequence'
	| 'variableSwitch'
	| 'presenceCondition'
	| 'virtualComponentEvent'
	| 'virtualComponentCondition'
	| 'virtualComponentAction'
	// Utility
	| 'setVariable'
	// Variable condition
	| 'variableCondition'
	// Switch / multi-branch
	| 'switchNode'
	// Editor-only (stored in flowGraph, not in scene if/do/else)
	| 'note';

export type TriggerSubType =
	| 'device'
	| 'time'
	| 'weather'
	| 'sun'
	| 'scene'
	| 'exchange'
	| 'alarm'
	| 'math'
	| 'manual';

export interface FlowNode {
	id: string;
	type: NodeType;
	position: { x: number; y: number };
	data: NodeData;
	validated: boolean;
	errors: string[];
}

// Device reference for triggers and actions
export interface DeviceReference {
	id: string;
	name: string;
	channel: string;
}

// Property configuration for device triggers
export interface PropertyConfig {
	id: string; // 'temperature', 'state', 'brightness', etc.
	name: string;
	type: 'numeric' | 'boolean' | 'enum' | 'string';
	constraints?: { min: number; max: number };
	values?: string[] | Array<{ value: any; label: string }>; // For enums - either simple strings or objects with value/label
	character?: string; // '%', 'C', etc.
}

// Condition type for triggers
export interface ConditionTypeConfig {
	id: 1 | 2 | 3; // 1=once, 2=any change, 3=repeatedly
	name: string;
	valid_for_min?: number;
}

// Time configuration for time-based triggers
export interface TimeConfig {
	type: 'timer' | 'daily' | 'once';
	time?: string;
	weekdays?: number[];
	interval?: number;
}

// Action item for device actions
export interface ActionItem {
	name: string; // 'turn', 'brightness', 'r', 'g', 'b', 'temp', etc.
	value: string | number;
	type?: string;
	slot?: string; // For devices with slots (e.g., diffuser: 'left'|'right')
}

export interface NodeData {
	label: string;
	icon?: string;
	color?: string;
	/** Palette row key (`paletteKey ?? type`) for readiness + default data; set when adding from palette. */
	paletteKey?: string;

	// Display label (auto-generated or manually overridden)
	displayLabel?: string;
	isLabelManuallyOverridden?: boolean;

	// Scene-compatible data - NEW ADDITION
	sceneData?: any; // Original scene component data for bidirectional sync
	isValid?: boolean;
	validationErrors?: string[];
	isConfigured?: boolean;
	requiresConfiguration?: boolean;
	/** Set only when persisted flowGraph metadata has no matching `_fid` scene semantics. */
	runtimeOrphan?: boolean;

	// Trigger/Condition node properties
	triggerType?: TriggerSubType;
	device?: DeviceReference;
	property?: PropertyConfig;
	operator?: 'eq' | 'lt' | 'gt';
	value?: string | number;
	conditionType?: ConditionTypeConfig;
	timeConfig?: TimeConfig;

	// Action node properties
	actions?: ActionItem[];
	delay?: { value: number; units: string };

	// Set Variable node properties
	variable?: { name: string; value: any };

	// Logic gate specific
	conditions?: any[];
	conditionCount?: number;
	expanded?: boolean;

	//Notifications
	notificationType?: string;
	notificationMessage?: string;

	// Original scene data for bidirectional conversion
	originalCondition?: any;
	originalAction?: any;

	[key: string]: unknown; // Allow additional properties
}

export interface FlowEdge {
	id: string;
	source: string;
	target: string;
	sourceHandle?: string;
	targetHandle?: string;
	type?: string;
	animated?: boolean;
	style?: string;
	class?: string;
	[key: string]: unknown;
}

export interface FlowGraph {
	nodes: FlowNode[];
	edges: FlowEdge[];
}

// Node colors matching the plan
export const NODE_COLORS: Record<string, string> = {
	// Core types
	trigger: '#2f7df6',
	condition: '#3b82f6',
	action: '#22c55e',
	// Condition subtypes (inherit from condition)
	timeCondition: '#3b82f6',
	manualCondition: '#3b82f6',
	weatherCondition: '#3b82f6',
	sunriseSunsetCondition: '#3b82f6',
	energyCondition: '#3b82f6',
	mathCondition: '#8b5cf6',
	sceneCondition: '#3b82f6',
	alarmCondition: '#3b82f6',
	scheduleCondition: '#6366f1',
	// Action subtypes (inherit from action)
	notificationAction: '#22c55e',
	groupAction: '#22c55e',
	sceneAction: '#22c55e',
	alarmAction: '#22c55e',
	scriptAction: '#6366f1',
	// Consolidated nodes
	logicGate: '#10b981',
	timer: '#8b5cf6',
	hysteresisCondition: '#14b8a6',
	actionSequence: '#8b5cf6',
	variableSwitch: '#e879f9',
	presenceCondition: '#06b6d4',
	virtualComponentEvent: '#0ea5e9',
	virtualComponentCondition: '#06b6d4',
	virtualComponentAction: '#8b5cf6',
	// Utility
	setVariable: '#f97316',
	note: '#f97316',
};

// Edge colors
export const EDGE_COLORS = {
	true: '#22c55e',
	false: '#ef4444',
	default: '#3b82f6', // Light blue
} as const;

export const PORT_LABELS = {
	true: '✓',
	false: '✗',
	input: '→',
	output: '→',
} as const;

// Validation interface for scene integration
export interface ValidationResult {
	isValid: boolean;
	errors: string[];
	warnings: string[];
}

// Specialized node data subtypes used by ScenePropertyBridge
export type TriggerNodeData = NodeData & {
	triggerType?: string;
};

export type ConditionNodeData = NodeData & {
	conditionType?: string;
};

export type ActionNodeData = NodeData & {
	actionType?: string;
};

// Generic alias for any flow node data
export type FlowNodeData = NodeData;
